import { useCallback } from 'react';
import { getFolderById, getFolders } from '../../utils/collectionFolders';
import { FETCH_URL_PER_TURN_MAX, FETCH_URL_TURN_LIMIT_ERROR } from '../../services/ai/fetchUrl';
import { redactVariables } from '../../services/ai/secretHeuristics';
import { requestFactsForModel } from '../../services/ai/requestFacts';
import { sendRequestViaTauri } from '../../utils/tauri';
import { formatSendRequestResult } from './formatSendRequestResult';
import { maskSecrets } from './maskSecrets';

/** Runs one tool call from the model against the app (props) or an extension handler. */
export function useAiToolExecutor({
  collections,
  environments,
  workspaceVariables,
  globalVariables,
  selectedRequest,
  onExecuteRequest,
  getCurrentEditorRequest,
  onRunCollection,
  onSetVariable,
  onCreateCollection,
  onCreateFolders,
  onCreateRequest,
  onMoveRequests,
  onGetLastResponse,
  onGenerateTests,
  onGeneratePreRequestScript,
  onUpdateRequest,
  getCollections,
  interactiveMode,
  extensionActions,
  fetchUrlTurnCountRef,
}) {
  return useCallback(async (toolName, toolInput) => {
    switch (toolName) {
      case 'send_request': {
        try {
          const result = onExecuteRequest
            ? await onExecuteRequest(toolInput, { interactive: interactiveMode })
            : await sendRequestViaTauri({
                method: toolInput.method || 'GET',
                url: toolInput.url,
                headers: toolInput.headers || {},
                body: toolInput.body || null,
              });
          return formatSendRequestResult(result);
        } catch (err) {
          return { error: err.message };
        }
      }
      case 'get_collections': {
        const cols = getCollections?.() ?? collections;
        return {
          collections: (cols || []).map((c) => ({
            id: c.id,
            name: c.name,
            folders: getFolders(c).map((f) => ({
              id: f.id,
              name: f.name,
              parent_id: f.parent_id ?? null,
            })),
            requests: (c.requests || []).map((r) => {
              const folder = r.folder_id ? getFolderById(c, r.folder_id) : null;
              return {
                id: r.id,
                name: r.name,
                method: r.method,
                url: r.url,
                type: r.type || 'http',
                folder_id: r.folder_id ?? null,
                folder_name: folder?.name ?? null,
              };
            }),
          })),
        };
      }
      case 'get_environments':
        return { environments: maskSecrets(environments) };
      case 'get_request': {
        const cols = getCollections?.() ?? collections;
        for (const col of (cols || [])) {
          const req = (col.requests || []).find((r) => String(r.id) === String(toolInput.request_id));
          if (req) return { request: req, collection_id: col.id, facts: requestFactsForModel(req, col) };
        }
        return { error: 'Request not found' };
      }
      case 'set_variable':
        if (onSetVariable) onSetVariable(toolInput.key, toolInput.value, toolInput.scope || 'environment');
        return { success: true };
      case 'get_workspace_variables':
        return { workspace_variables: redactVariables(workspaceVariables) };
      case 'get_global_variables':
        return { global_variables: redactVariables(globalVariables) };
      case 'get_current_request':
        if (getCurrentEditorRequest) return getCurrentEditorRequest();
        // Never the raw record (headers, auth, body): the same fields the
        // editor path returns.
        return selectedRequest
          ? { request: { id: selectedRequest.id, name: selectedRequest.name, method: selectedRequest.method, url: selectedRequest.url } }
          : { error: 'No active request tab' };
      case 'run_collection':
        return onRunCollection
          ? await onRunCollection(toolInput, { interactive: interactiveMode })
          : { error: 'Run collection not available' };
      case 'get_last_response':
        return onGetLastResponse ? await onGetLastResponse(toolInput) : { error: 'Last response not available' };
      case 'generate_tests':
        return onGenerateTests ? await onGenerateTests(toolInput) : { error: 'Test generation not available' };
      case 'generate_pre_request_script':
        return onGeneratePreRequestScript
          ? await onGeneratePreRequestScript(toolInput)
          : { error: 'Pre-request script generation not available' };
      case 'update_request':
        return onUpdateRequest ? await onUpdateRequest(toolInput) : { error: 'Update request not available' };
      case 'create_collection':
        return onCreateCollection ? await onCreateCollection(toolInput.name) : { error: 'Create collection not available' };
      case 'create_folders':
        return onCreateFolders ? await onCreateFolders(toolInput) : { error: 'Create folders not available' };
      case 'create_request':
        return onCreateRequest ? await onCreateRequest(toolInput) : { error: 'Create request not available' };
      case 'move_requests':
        return onMoveRequests ? await onMoveRequests(toolInput) : { error: 'Move requests not available' };
      // Extension tools (Task Manager / DB Manager / Documents) — dispatched by
      // name through the extensionActions map wired in App.js.
      case 'get_boards':
      case 'get_board':
      case 'create_board':
      case 'open_board':
      case 'rename_board':
      case 'delete_board':
      case 'create_list':
      case 'create_task':
      case 'update_task':
      case 'write_task_description':
      case 'db_list_connections':
      case 'db_get_schema':
      case 'db_run_sql':
      case 'terminal_list_connections':
      case 'terminal_run_command':
      case 'get_documents':
      case 'read_document':
      case 'create_doc_folder':
      case 'create_document':
      case 'open_document':
      case 'rename_document':
      case 'delete_document':
      case 'write_document':
      // Workspace tools (history, environment lifecycle, auth) and the web
      // discovery tools (web_search, fetch_url — useAiDiscoveryActions) arrive
      // through the same handler map as the extension tools.
      // eslint-disable-next-line no-fallthrough
      case 'get_history':
      case 'create_environment':
      case 'duplicate_environment':
      case 'switch_environment':
      case 'set_auth':
      case 'import_spec':
      case 'generate_code':
      case 'create_mock_example':
      case 'get_git_status':
      case 'ftp_list_connections':
      case 'ftp_list_dir':
      case 'create_stream_connection':
      case 'web_search':
      case 'fetch_url': {
        // Hard per-turn cap (NB2): the 4th fetch in one turn is refused here,
        // before any HTTP, whatever the model or a fetched page asks for.
        if (toolName === 'fetch_url') {
          if (fetchUrlTurnCountRef.current >= FETCH_URL_PER_TURN_MAX) {
            return { error: FETCH_URL_TURN_LIMIT_ERROR };
          }
          fetchUrlTurnCountRef.current += 1;
        }
        const handler = extensionActions?.[toolName];
        if (!handler) return { error: `Tool not available: ${toolName}` };
        try {
          return await handler(toolInput, { interactive: interactiveMode });
        } catch (err) {
          return { error: err.message || String(err) };
        }
      }
      default:
        return { error: `Unknown tool: ${toolName}` };
    }
  }, [
    collections,
    environments,
    workspaceVariables,
    globalVariables,
    selectedRequest,
    onExecuteRequest,
    getCurrentEditorRequest,
    onRunCollection,
    onSetVariable,
    onCreateCollection,
    onCreateFolders,
    onCreateRequest,
    onMoveRequests,
    onGetLastResponse,
    onGenerateTests,
    onGeneratePreRequestScript,
    onUpdateRequest,
    getCollections,
    interactiveMode,
    extensionActions,
    fetchUrlTurnCountRef,
  ]);
}
